import React from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  ButtonBase,
  Collapse,
  Divider,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { BodyText, CaptionText, CardTitle, OverlineText, SectionTitle } from '../typography';
import type { SxProps, Theme } from '@mui/material/styles';
import HelpTooltipTrigger from '../common/HelpTooltipTrigger';
import type { HelpValue } from '../common/helpModel';
import { borderRadius, getControlBorderColor, getGlassCard, layoutGap } from '../../../theme/theme';
import { WorkspaceStatusPill } from './WorkflowWorkspacePrimitives';

export type WorkflowVisualVariant = 'wizard' | 'workspace';

export type ReadinessTone =
  | 'ready'
  | 'missing'
  | 'optional'
  | 'set'
  | 'blocked'
  | 'disabled'
  | 'derived';

interface ToneConfig {
  color: string;
  background: string;
  border: string;
}

export interface WorkflowChromePalette {
  shellBackground: string;
  shellBorder: string;
  surfaceBackground: string;
  surfaceBorder: string;
  tileBackground: string;
  tileBorder: string;
  tileSelectedBackground: string;
  tileSelectedBorder: string;
  tileMutedBackground: string;
  accentBackground: string;
  subtleBackground: string;
}

export const getWorkflowChromePalette = (
  theme: Theme,
  variant: WorkflowVisualVariant = 'wizard'
): WorkflowChromePalette => {
  const isWorkspace = variant === 'workspace';

  return {
    shellBackground: isWorkspace
      ? alpha(theme.palette.background.default, 0.08)
      : alpha(theme.palette.background.paper, 0.94),
    shellBorder: isWorkspace
      ? alpha(theme.palette.divider, 0.16)
      : alpha(theme.palette.primary.main, 0.14),
    surfaceBackground: isWorkspace
      ? alpha(theme.palette.background.paper, 0.34)
      : alpha(theme.palette.background.paper, 0.34),
    surfaceBorder: isWorkspace
      ? alpha(theme.palette.divider, 0.12)
      : alpha(theme.palette.divider, 0.13),
    tileBackground: isWorkspace
      ? alpha(theme.palette.background.paper, 0.22)
      : alpha(theme.palette.background.default, 0.1),
    tileBorder: alpha(theme.palette.divider, isWorkspace ? 0.1 : 0.11),
    tileSelectedBackground: alpha(theme.palette.primary.main, isWorkspace ? 0.08 : 0.07),
    tileSelectedBorder: alpha(theme.palette.primary.main, 0.2),
    tileMutedBackground: alpha(theme.palette.background.paper, isWorkspace ? 0.14 : 0.12),
    accentBackground: alpha(theme.palette.primary.main, 0.12),
    subtleBackground: alpha(theme.palette.background.default, isWorkspace ? 0.18 : 0.24),
  };
};

const getToneConfig = (theme: Theme, tone: ReadinessTone): ToneConfig => {
  switch (tone) {
    case 'ready':
    case 'set':
      return {
        color: theme.palette.success.light,
        background: alpha(theme.palette.success.main, 0.14),
        border: alpha(theme.palette.success.main, 0.22),
      };
    case 'missing':
    case 'blocked':
      return {
        color: theme.palette.warning.light,
        background: alpha(theme.palette.warning.main, 0.16),
        border: alpha(theme.palette.warning.main, 0.24),
      };
    case 'optional':
    case 'disabled':
    case 'derived':
    default:
      return {
        color: theme.palette.text.secondary,
        background: alpha(theme.palette.background.default, 0.24),
        border: alpha(theme.palette.divider, 0.18),
      };
  }
};

export interface SectionSummaryItem {
  key: string;
  title: string;
  statusLabel: string;
  statusTone: ReadinessTone;
  detail?: ReactNode;
  meta?: ReactNode;
  help?: HelpValue;
  helpLabel?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  role?: string;
  ariaSelected?: boolean;
  ariaChecked?: boolean;
  ariaControls?: string;
  ariaDisabled?: boolean;
  id?: string;
  tabIndex?: number;
  variant?: WorkflowVisualVariant;
}

interface InlineReadinessBadgeProps {
  label: string;
  tone: ReadinessTone;
  compact?: boolean;
}

export const InlineReadinessBadge: React.FC<InlineReadinessBadgeProps> = ({
  label,
  tone,
  compact = false,
}) => {
  const workspaceTone =
    tone === 'ready' || tone === 'set'
      ? 'success'
      : tone === 'missing' || tone === 'blocked'
        ? 'warning'
        : 'neutral';

  return (
    <WorkspaceStatusPill label={label} tone={workspaceTone} />
  );
};

interface WorkflowStepShellProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
  variant?: WorkflowVisualVariant;
}

export const WorkflowStepShell: React.FC<WorkflowStepShellProps> = ({
  children,
  sx,
  variant = 'wizard',
}) => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: variant === 'workspace' ? 0.9 : 0.85,
        pb: 0.6,
        '& > *': {
          scrollMarginTop: theme.spacing(1.5),
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

interface StepHeaderProps {
  title: string;
  purpose: string;
  context?: string;
}

export const StepHeader: React.FC<StepHeaderProps> = ({ title, purpose, context }) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'grid', gap: layoutGap.micro }}>
      <Typography
        variant="h5"
        component="h1"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {title}
      </Typography>
      <BodyText secondary sx={{ lineHeight: 1.35 }}>
        {purpose}
      </BodyText>
      {context && (
        <CaptionText sx={{ color: theme.palette.text.secondary, opacity: 0.9 }}>
          {context}
        </CaptionText>
      )}
    </Box>
  );
};

interface SetupConfirmationStripProps {
  title: string;
  description: ReactNode;
  control?: ReactNode;
  statusLabel: string;
  statusTone: ReadinessTone;
  blocker?: string | null;
  action?: ReactNode;
  variant?: WorkflowVisualVariant;
  eyebrow?: string;
  layout?: 'stacked' | 'sidebar';
}

export const SetupConfirmationStrip: React.FC<SetupConfirmationStripProps> = ({
  title,
  description,
  control,
  statusLabel,
  statusTone,
  blocker,
  action,
  variant = 'wizard',
  eyebrow = 'Setup confirmation',
  layout = 'stacked',
}) => {
  const theme = useTheme();
  const palette = getWorkflowChromePalette(theme, variant);
  const cardStyles = React.useMemo(() => getGlassCard(theme), [theme]);
  const sidebar = layout === 'sidebar';

  return (
    <Paper
      elevation={0}
      sx={{
        ...cardStyles,
        px: { xs: 1, md: 1.15 },
        py: 0.85,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${palette.surfaceBorder}`,
        backgroundColor: palette.surfaceBackground,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gap: layoutGap.row,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: sidebar
              ? '1fr'
              : { xs: '1fr', md: 'minmax(0, 1fr) auto' },
            alignItems: 'start',
            gap: sidebar ? layoutGap.dense : layoutGap.row,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {eyebrow ? (
              <OverlineText uppercase={false} sx={{ color: theme.palette.text.secondary, lineHeight: 1.2 }}>
                {eyebrow}
              </OverlineText>
            ) : null}
            <SectionTitle sx={{ mt: 0.05, lineHeight: 1.25 }}>
              {title}
            </SectionTitle>
            <CaptionText sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.2, lineHeight: 1.35 }}>
              {description}
            </CaptionText>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: sidebar ? 'flex-start' : { xs: 'flex-start', md: 'flex-end' },
              gap: layoutGap.dense,
              flexWrap: 'wrap',
            }}
          >
            {action}
            <InlineReadinessBadge label={statusLabel} tone={statusTone} />
          </Box>
        </Box>

        {(control || blocker) && <Divider sx={{ opacity: 0.28 }} />}

        {control && <Box sx={{ minWidth: 0 }}>{control}</Box>}

        {blocker && (
          <CaptionText
            sx={{ color: theme.palette.warning.light, fontWeight: 700, lineHeight: 1.5 }}
          >
            {blocker}
          </CaptionText>
        )}
      </Box>
    </Paper>
  );
};

interface SectionSwitcherProps {
  ariaLabel: string;
  items: SectionSummaryItem[];
  columns?: string | Record<string, string>;
  variant?: WorkflowVisualVariant;
  presentation?: 'tiles' | 'tabs';
  /** Trailing content pinned to the end of the tab bar (tabs presentation). */
  action?: ReactNode;
}

export const SectionSwitcher: React.FC<SectionSwitcherProps> = ({
  ariaLabel,
  items,
  columns = { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  variant = 'wizard',
  presentation = 'tiles',
  action,
}) => {
  const theme = useTheme();
  const palette = getWorkflowChromePalette(theme, variant);

  if (presentation === 'tabs') {
    return (
      <Box
        role="tablist"
        aria-label={ariaLabel}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: layoutGap.dense,
          minWidth: 0,
          px: 0.35,
          py: 0.3,
          borderRadius: `${borderRadius.md}px`,
          border: `1px solid ${palette.surfaceBorder}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.18),
          overflowX: 'auto',
        }}
      >
        {items.map(({ key, title, statusLabel, statusTone, selected, disabled, onClick, role, ariaSelected, ariaControls, id, tabIndex, help, helpLabel }) => {
          const toneConfig = getToneConfig(theme, statusTone);
          return (
            <ButtonBase
              component="div"
              key={key}
              role={role}
              id={id}
              aria-selected={ariaSelected}
              aria-controls={ariaControls}
              disabled={disabled}
              tabIndex={tabIndex}
              onClick={onClick}
              sx={{
                minHeight: 30,
                flex: '0 0 auto',
                px: 0.85,
                borderRadius: `${borderRadius.sm}px`,
                color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
                backgroundColor: selected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                border: `1px solid ${getControlBorderColor(theme)}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: layoutGap.dense,
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }}
            >
              <CaptionText sx={{ fontWeight: 700, lineHeight: 1 }}>
                {title}
              </CaptionText>
              <HelpTooltipTrigger
                label={helpLabel || `Help for ${title}`}
                help={help}
                placement="bottom-start"
                sx={{ flex: '0 0 auto' }}
              />
              <Box
                aria-label={statusLabel}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: toneConfig.color,
                  boxShadow: `0 0 0 2px ${toneConfig.background}`,
                }}
              />
            </ButtonBase>
          );
        })}
        {action ? <Box sx={{ ml: 'auto', flex: '0 0 auto' }}>{action}</Box> : null}
      </Box>
    );
  }

  return (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      sx={{
        display: 'grid',
        gridTemplateColumns: columns,
        gap: layoutGap.row,
      }}
    >
      {items.map(({ key, ...item }) => (
        <SectionSummaryTile key={key} variant={variant} {...item} />
      ))}
    </Box>
  );
};

export const SectionSummaryTile: React.FC<SectionSummaryItem> = ({
  title,
  statusLabel,
  statusTone,
  detail,
  meta,
  help,
  helpLabel,
  selected = false,
  disabled = false,
  onClick,
  role,
  ariaSelected,
  ariaChecked,
  ariaControls,
  ariaDisabled,
  id,
  tabIndex,
  variant = 'wizard',
}) => {
  const theme = useTheme();
  const palette = getWorkflowChromePalette(theme, variant);
  const cardStyles = React.useMemo(() => getGlassCard(theme), [theme]);
  const isInteractive =
    Boolean(onClick) ||
    Boolean(role) ||
    typeof ariaSelected !== 'undefined' ||
    typeof tabIndex !== 'undefined';
  const tileSx = {
    ...cardStyles,
    width: '100%',
    textAlign: 'left',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    minHeight: 58,
    borderRadius: `${borderRadius.md}px`,
    border: `1px solid ${getControlBorderColor(theme)}`,
    backgroundColor: selected
      ? palette.tileSelectedBackground
      : disabled
        ? palette.tileMutedBackground
        : palette.tileBackground,
    px: 0.9,
    py: 0.7,
    opacity: disabled ? 0.58 : 1,
    transition: 'border-color 0.16s ease, background-color 0.16s ease',
    ...(isInteractive
      ? {
          cursor: disabled ? 'default' : 'pointer',
          '&:hover': disabled
            ? undefined
            : {
                borderColor: getControlBorderColor(theme),
                backgroundColor: selected
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.04),
              },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        }
      : {}),
  } satisfies SxProps<Theme>;
  const content = (
    <Box sx={{ width: '100%', display: 'grid', gap: layoutGap.micro }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: layoutGap.row }}>
        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: layoutGap.dense }}>
          <CardTitle sx={{ lineHeight: 1.25, minWidth: 0, overflowWrap: 'anywhere' }}>
            {title}
          </CardTitle>
          <HelpTooltipTrigger
            label={helpLabel || `Help for ${title}`}
            help={help}
            placement="bottom-start"
            sx={{ flex: '0 0 auto' }}
          />
        </Box>
        <Box sx={{ flex: '0 0 auto' }}>
          <InlineReadinessBadge label={statusLabel} tone={statusTone} compact />
        </Box>
      </Box>
      {detail && (
        <CaptionText component="div" sx={{ color: theme.palette.text.secondary, lineHeight: 1.3 }}>
          {detail}
        </CaptionText>
      )}
      {meta && (
        <CaptionText component="div" sx={{ color: theme.palette.text.secondary, opacity: 0.86 }}>
          {meta}
        </CaptionText>
      )}
    </Box>
  );

  if (!isInteractive) {
    return <Box sx={tileSx}>{content}</Box>;
  }

  if (help) {
    return (
      <ButtonBase
        component="div"
        role={role}
        id={id}
        aria-selected={ariaSelected}
        aria-checked={ariaChecked}
        aria-controls={ariaControls}
        aria-disabled={ariaDisabled}
        disabled={disabled}
        tabIndex={tabIndex}
        onClick={onClick}
        sx={tileSx}
      >
        {content}
      </ButtonBase>
    );
  }

  return (
    <ButtonBase
      role={role}
      id={id}
      aria-selected={ariaSelected}
      aria-checked={ariaChecked}
      aria-controls={ariaControls}
      aria-disabled={ariaDisabled}
      disabled={disabled}
      tabIndex={tabIndex}
      onClick={onClick}
      sx={tileSx}
    >
      {content}
    </ButtonBase>
  );
};

interface ActiveSectionHeaderProps {
  title: string;
  description: string;
  statusLabel: string;
  statusTone: ReadinessTone;
  aside?: ReactNode;
  variant?: WorkflowVisualVariant;
}

export const ActiveSectionHeader: React.FC<ActiveSectionHeaderProps> = ({
  title,
  description,
  statusLabel,
  statusTone,
  aside,
  variant = 'wizard',
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <SectionTitle component="h2" sx={{ mb: 0.15 }}>
          {title}
        </SectionTitle>
        <CaptionText
          sx={{ color: theme.palette.text.secondary, maxWidth: variant === 'workspace' ? 560 : 'none', display: 'block', lineHeight: 1.4 }}
        >
          {description}
        </CaptionText>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, flexWrap: 'wrap' }}>
        {aside}
        <InlineReadinessBadge label={statusLabel} tone={statusTone} />
      </Box>
    </Box>
  );
};

interface EditorSurfaceProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
  variant?: WorkflowVisualVariant;
}

export const EditorSurface: React.FC<EditorSurfaceProps> = ({
  children,
  sx,
  variant = 'wizard',
}) => {
  const theme = useTheme();
  const cardStyles = React.useMemo(() => getGlassCard(theme), [theme]);

  return (
    <Paper
      elevation={0}
      sx={{
        ...cardStyles,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.18),
        px: variant === 'workspace' ? 1.05 : 1.1,
        py: variant === 'workspace' ? 0.9 : 1,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
};

interface WorkflowNoticeProps {
  severity: 'info' | 'warning' | 'success' | 'error';
  children: ReactNode;
  title?: ReactNode;
  variant?: WorkflowVisualVariant;
  sx?: SxProps<Theme>;
  /** Custom leading icon; replaces the severity default icon. */
  icon?: ReactNode;
}

export const WorkflowNotice: React.FC<WorkflowNoticeProps> = ({
  severity,
  children,
  title,
  variant = 'wizard',
  sx,
  icon,
}) => {
  const theme = useTheme();
  const palette =
    severity === 'success'
      ? theme.palette.success
      : severity === 'info'
        ? theme.palette.info
        : severity === 'error'
          ? theme.palette.error
          : theme.palette.warning;

  return (
    <Alert
      severity={severity}
      icon={icon}
      sx={{
        py: 0.35,
        px: 0.85,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${alpha(palette.main, variant === 'workspace' ? 0.22 : 0.28)}`,
        backgroundColor: alpha(palette.main, variant === 'workspace' ? 0.1 : 0.12),
        '& .MuiAlert-icon': {
          fontSize: 18,
          alignItems: title ? 'flex-start' : 'center',
          // Zero the vertical padding symmetrically: clearing only the top
          // leaves MUI's 7px bottom padding, which pushes the icon above the
          // optically centered text.
          py: title ? 0.05 : 0,
        },
        ...sx,
      }}
    >
      {title && <AlertTitle sx={{ mb: 0.35 }}>{title}</AlertTitle>}
      {children}
    </Alert>
  );
};

interface MappingFieldGridProps {
  children: ReactNode;
  columns?: string | Record<string, string>;
  sx?: SxProps<Theme>;
}

export const MappingFieldGrid: React.FC<MappingFieldGridProps> = ({
  children,
  columns = { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
  sx,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: columns,
      gap: layoutGap.row,
      ...sx,
    }}
  >
    {children}
  </Box>
);

interface AdvancedOptionRowProps {
  title: string;
  summary: string;
  statusLabel?: string;
  statusTone?: ReadinessTone;
  action?: ReactNode;
  expanded?: boolean;
  children?: ReactNode;
}

export const AdvancedOptionRow: React.FC<AdvancedOptionRowProps> = ({
  title,
  summary,
  statusLabel,
  statusTone = 'optional',
  action,
  expanded = false,
  children,
}) => {
  const theme = useTheme();
  const cardStyles = React.useMemo(() => getGlassCard(theme), [theme]);

  return (
    <Paper
      elevation={0}
      sx={{
        ...cardStyles,
        px: 1.1,
        py: 0.85,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.22),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <CardTitle component="h3">
            {title}
          </CardTitle>
          <CaptionText sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.2 }}>
            {summary}
          </CaptionText>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense, flexWrap: 'wrap' }}>
          {action}
          {statusLabel && <InlineReadinessBadge label={statusLabel} tone={statusTone} compact />}
        </Box>
      </Box>
      {children && (
        <Collapse in={expanded}>
          <Divider sx={{ my: 0.85, opacity: 0.35 }} />
          {children}
        </Collapse>
      )}
    </Paper>
  );
};

interface SectionAlertProps {
  ready: boolean;
  text: string;
}

export const SectionAlert: React.FC<SectionAlertProps> = ({ ready, text }) => (
  <WorkflowNotice severity={ready ? 'success' : 'warning'} sx={{ mb: 1 }}>
    {text}
  </WorkflowNotice>
);
